import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ConditionalRender from 'components/ConditionalRender'
import { Select } from '@driftt/tide-selects';
import { Table } from '@driftt/tide-table'
import { fetchAppScopes, fetchAppScopeDescriptions, updateAppScopes } from 'api/developerApi'
import { isEmpty, contains, without, groupBy, forEachObjIndexed } from 'ramda'
import trashBinSrc from './trashBin.svg'
import './style.css'
import * as _ from 'lodash'

const GDPR_SCOPES = ['gdpr_read', 'gdpr_write'];
const ACTIONS_LABEL = ''
const SCOPE_DESCRIPTION_LABEL = ''
const resolveScopesTableColumns = (label) => [
  { label },
  { label: SCOPE_DESCRIPTION_LABEL },
  { label: ACTIONS_LABEL }
]

const DeleteButton = ({ onClick }) => (
  <img
    className="app-scopes-delete-icon"
    src={trashBinSrc}
    onClick={onClick}
    alt="delete"
  />
)


class OAuthScopes extends Component {

  static propTypes = {
    appId: PropTypes.string.isRequired,
    showErrorNotification: PropTypes.func.isRequired
  }

  state = {
    isLoading: true,
    scopes: [],
    scopeDescriptionGroups: []
  }

  componentDidMount() {
    this.fetchData()
  }

  fetchData = () => {
    const { appId, showErrorNotification } = this.props

    const onDone = ([scopeDescriptionGroups, scopes]) => {
      this.setState({ scopeDescriptionGroups, scopes, isLoading: false })
    }
    const onError = () => {
      showErrorNotification('Oops, we couldn’t fetch some data. Please refresh the page.')
      this.setState({ isLoading: false })
    }

    Promise.all([
      fetchAppScopeDescriptions(),
      fetchAppScopes(appId)
    ])
    .then(onDone)
    .catch(onError)
  }

  resolveAvailableScopes = () => {
    const { scopes, scopeDescriptionGroups } = this.state

    if (isEmpty(scopeDescriptionGroups))
      return []

    const scopeGroups = scopeDescriptionGroups.map(group => group.scopes)
    const flattenedScopeGroups = _.flatten(scopeGroups)
    const allScopeNames = _.map(flattenedScopeGroups, 'name')

    return without(scopes, allScopeNames)
  }

  resolveAvailableScopeOptions = () => {
    return this.resolveAvailableScopes()
      .map(scopeName => ({
        value: scopeName,
        label: scopeName
      }))
  }

  addScope = ({ value }) => {
    const { appId, showErrorNotification } = this.props
    const { scopes } = this.state
    const updatedScopes = [...scopes, value]

    const onError = () => {
      showErrorNotification('Ooops, something went wrong while adding that scope. Please try again.')
      this.setState({ scopes })
    }

    const onStateSet = () => updateAppScopes({ appId, scopes: updatedScopes })
      .catch(onError)

    this.setState({ scopes: updatedScopes }, onStateSet)
  }

  removeScope = (scope) => {
    const { appId, showErrorNotification } = this.props
    const { scopes } = this.state
    const updatedScopes = without([scope], scopes)

    const onError = () => {
      showErrorNotification('Ooops, something went wrong while removing that scope. Please try again.')
      this.setState({ scopes })
    }

    const onStateSet = () => updateAppScopes({ appId, scopes: updatedScopes })
      .catch(onError)

    this.setState({ scopes: updatedScopes }, onStateSet)
  }

  resolveScopesTableRows = (scopeAndDescriptionPairs) => {
    var mappedPairs = scopeAndDescriptionPairs.map(pair => [
      { content: pair.scope },
      { content: pair.description },
      { content:
        <DeleteButton onClick={() => this.removeScope(pair.scope)} />
      }
    ])

    return mappedPairs
  }

  resolveScopeTables = () => {
    const { scopes, scopeDescriptionGroups } = this.state

    if (isEmpty(scopes) || isEmpty(scopeDescriptionGroups))
      return []

    const hasScope = (group, scope) => {
      const flatScopes = group.scopes.map(s => s.name)
      return contains(scope, flatScopes)
    }

    const toScopeDescriptors = (scope) => {
      const group = scopeDescriptionGroups.find(group => hasScope(group, scope))
      const groupScope = group.scopes.find(s => s.name === scope)

      return {
        scope,
        scopeDescription: groupScope.description,
        groupName: group.name
      }
    }

    const scopeDescriptors = scopes.map(toScopeDescriptors)
    const scopeDescriptorGroups = groupBy(g => g.groupName, scopeDescriptors)

    const createGroupTable = (scopeDescriptorGroup, groupName) => {
      const columns = resolveScopesTableColumns(groupName)
      const scopeAndDescriptionPairs = scopeDescriptorGroup.map(g => ({
        scope: g.scope,
        description: g.scopeDescription
      }))

      const rows = this.resolveScopesTableRows(scopeAndDescriptionPairs)

      return (
        <Table
          key={groupName}
          className="app-oauth-scopes-table"
          columns={columns}
          data={rows.map(row => {
            return {data: row}
          })}
        />
      )
    }

    const tables = []

    forEachObjIndexed((scopeDescriptorGroup, groupName) => {
      const table = createGroupTable(scopeDescriptorGroup, groupName)
      tables.push(table)
    })(scopeDescriptorGroups)

    return tables
  }

  render() {
    const { isSelectorDisabled } = this.props
    const availableScopeOptions = this.resolveAvailableScopeOptions()
    const scopeTables = this.resolveScopeTables()

    const { scopes } = this.state;
    const hasGDPRScopes = scopes.filter(value => -1 !== GDPR_SCOPES.indexOf(value)).length > 0;

    return (
      <div className="app-oauth-scopes">
        <span className="app-oauth-section-title">
          Drift Scopes
        </span>
        <p className="app-oauth-section-description">
          Scopes define the <b>API methods and Webhooks</b> this app is allowed to use, and thus which infomation and capabilities are available on a Drift environment it’s installed on.
        </p>
        <p className="app-oauth-section-description">
          If your app is submitted to the Drift App Directory, we’ll review your reasons for requesting each scope. After your app is listed in the Directory, it will only be able to use permission scopes Drift has approved.
        </p>

        {hasGDPRScopes && <p className="app-oauth-section-has-gdpr">
          You have enabled GDPR scopes for your app. These scopes are intended for personal use only.
          If you intend to make this app public for other Drift customers, you will need to remove these scopes for public use or contact us for explicit approval.
        </p>}

        <ConditionalRender
          condition={!isEmpty(availableScopeOptions)}
          className="app-oauth-scopes-select">
          <Select
            label="Add Permissions by Scopes"
            placeholder="Select permission scopes"
            size="medium"
            searchable={false}
            options={availableScopeOptions}
            disabled={isSelectorDisabled}
            onChange={this.addScope}
            className="app-oauth-scopes-select-component"
          />
        </ConditionalRender>

        <ConditionalRender condition={!isEmpty(scopeTables)}>
          {scopeTables}
        </ConditionalRender>
      </div>
    )
  }
}

export default OAuthScopes
